<template>
  <div v-if="showTest" id="app" style="min-height: 900px" class="app">
    <div class="app__main" style="min-height: 900px">
      <router-view class="app__content" style="min-height: 900px" />
    </div>
  </div>

  <div
    v-else
    id="app"
    style="min-height: 900px"
    class="app"
    :class="{ 'social-d-lp': socialDummyLp }"
  >
    <AppHeaderAlternative v-if="showAlternativeLayout" />
    <div v-else class="app__header">
      <RegistrationLPHeader v-if="showShortHeader" />
      <AppHeader v-else />
    </div>

    <div class="app__main" style="min-height: 900px">
      <router-view class="app__content" style="min-height: 900px" />
    </div>

    <AppFooter v-if="showFooter" />

    <AppModals />
    <AppQuiz v-if="shouldLoadQuiz" />
    <NotificationOverlay v-if="isLoggedIn" />
    <ShopOverlay v-if="showShopOverlay" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import breakpointsMixin from '@/mixin/breakpoints';
import syncWithSymfonyMixin from '@/mixin/syncWithSymfony';
import { bus } from '@/main';
import { isWebpSupported } from '@/helpers/webp';
import AppModals from '@/components/AppModals.vue';
import AppHeader from '@/components/App/Header.vue';
import AppFooter from '@/components/App/AppFooter.vue';
import { trackGaAccountActivation } from '@/helpers/tagmanager';

export default {
  name: 'app',
  components: {
    AppQuiz: () => import(/* webpackChunkName: "app-quiz" */ '@/components/AppQuiz.vue'),
    AppModals,
    AppHeader,
    AppFooter,
    NotificationOverlay: () =>
      import(
        /* webpackChunkName: "notification-overlay" */ '@/components/NotificationOverlay/NotificationOverlay.vue'
      ),
    RegistrationLPHeader: () =>
      import(
        /* webpackChunkName: "registration-lp-header" */ '@/components/RegistrationLP/RegistrationLPHeader.vue'
      ),
    AppHeaderAlternative: () =>
      import(
        /* webpackChunkName: "app-header-alternative" */ '@/components/App/HeaderAlternative.vue'
      ),
    ShopOverlay: () =>
      import(/* webpackChunkName: "shop-overlay" */ '@/components/ShopOverlay/ShopOverlay.vue'),
  },
  data: () => ({
    showFooter: false,
    maintenanceMode: false,
  }),

  mixins: [breakpointsMixin, syncWithSymfonyMixin],
  metaInfo() {
    return {
      title: this.translate('Jobs, Headhunters and Career Services'),
      titleTemplate: '%s | JobLeads',
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.translate(
            'Find executive positions. JobLeads is your premium service for senior-level career opportunities.',
          ),
        },
        {
          vmid: 'robots',
          name: 'robots',
          content: 'noindex,nofollow',
        },
      ],
      link: [
        {
          rel: 'canonical',
          vmid: 'canonical',
          href: this.canonical,
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      user: 'user/info',
      isLoggedIn: 'user/isLoggedIn',
      isMdMax: 'breakpoints/isMdMax',
      showShopOverlay: 'showShopOverlay',
    }),
    showShortHeader() {
      return this.$route.name === 'registration-sea' || this.$route.name === 'registration-job';
    },
    showAlternativeLayout() {
      return this.$route.name === 'registration' && ['69'].includes(this.$route.query.campaignId);
    },
    showTest() {
      return this.$route.name === 'geheimnis2';
    },
    shouldLoadQuiz() {
      return !!(this.isLoggedIn || this.$route.query.quiz);
    },
    socialDummyLp() {
      return this.$route.params.campaignId === '70';
    },
    canonical() {
      return window.location.origin + this.$route.path;
    },
  },
  methods: {
    ...mapActions({
      destroyUser: 'user/destroy',
      clearLoggedUserData: 'user/clearLoggedUserData',
      setCurrentCountry: 'country/setCurrentCountry',
      fetchResumeBuilderUserConfig: 'resumeBuilder/fetchUserConfig',
    }),
    ...mapMutations({
      setWebpSupported: 'setWebpSupported',
    }),
    initUserState(data) {
      const { user } = data || {};
      this.$store.dispatch('user/init', user);

      // if (Boolean(user?.id) === false && process.env.NODE_ENV === 'development') {
      //   this.$store.dispatch('user/fetch').catch(() => null);
      // }
    },
    showMaintenancePage(html) {
      if (html) {
        const maintenanceHTML = document.open('text/html', 'replace');
        maintenanceHTML.write(html);
        maintenanceHTML.close();
      } else {
        document.location.href = '/maintenance.html';
      }
    },
    shouldRedirect() {
      const _isLimitedLoginModalOpen = () => {
        return this.$route.query?.modal === 'limited-login';
      };

      return !_isLimitedLoginModalOpen();
    },
  },
  created() {
    this.setCurrentCountry();
    bus.$on('user-unauthorized', async () => {
      window.location.href = '/?modal=login';
      // this.destroyUser();
      // this.clearLoggedUserData();

      // if (this.shouldRedirect()) {
      //   bus.$emit('open-modal', 'login');
      // }
    });
    bus.$on('maintenance-mode', () => {
      if (!this.maintenanceMode) {
        this.maintenanceMode = true;
        fetch('/maintenance.html')
          .then((res) => res.text())
          .then((html) => this.showMaintenancePage(html))
          .catch((err) => {
            console.log(err);
            console.log(err?.response);
            console.log(err?.text());
            this.showMaintenancePage();
          });
      }
    });
  },
  async beforeMount() {
    const { appSetup } = window;
    await this.$store.dispatch('init', appSetup);

    this.initUserState(appSetup);
    if (!process.env.VUE_APP_DISABLE_WEBP) {
      this.setWebpSupported(await isWebpSupported());
    }

    // set prerender flag to false
    window.prerenderReady = false;

    // Load user config for resume builder of logged in
    // The config will be checked to enable resume builder routes
    if (this.isLoggedIn) {
      this.fetchResumeBuilderUserConfig();
    }
  },
  watch: {
    shouldLoadQuiz: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.$store.dispatch('quiz/init');
        }
      },
    },
    showShopOverlay(value) {
      value
        ? document.body.classList.add('overflow-y-hidden')
        : document.body.classList.remove('overflow-y-hidden');
    },
  },

  mounted() {
    // This is to fix CLS for shifting footer on async loeaded content
    bus.$on('show-footer', (data) => {
      this.showFooter = data;
    });

    // temp for development
    setTimeout(() => {
      if (this.user?.hasRightToViewBlockedPremiumMessage) {
        bus.$emit('open-modal', 'blocked-premium');
      }
    }, 1000);

    setTimeout(() => {
      if (this.user?.firstLoginAfterActivationByUser) {
        trackGaAccountActivation();
      }
    }, 1000);

    if (!this.isMdMax) {
      this.$store.dispatch('country/getCountriesWithGeoSupport');
    }

    setTimeout(() => {
      if (
        ![
          'search-jobs',
          'search-jobs-slug',
          'search-headhunter-jobs-slug',
          'search-headhunter',
        ].includes(this.$route.name)
      ) {
        this.$store.dispatch('salary/getSalarySliderOptions');
      }
    }, 500);
  },
};
</script>

<style lang="scss" src="./app.scss" />
